// import { RecipeCard } from "@/features/RecipeCard/RecipeCard";
import { RecipeCard2 } from "@/features/RecipeCard/RecipeCard2";
import { NoResults } from "@/features/Recipes-renamed/NoResults";
import { RecipesFilterContainer } from "@/features/Recipes-renamed/RecipesFilterContainer";
import { RecipesListGrid } from "@/features/Recipes-renamed/RecipesListGrid";
import { useGetAppliedFiltersFromLocation } from "@/hooks/useGetAppliedFiltersFromLocation";
import { useGetRecipes } from "@/hooks/useGetRecipes";
import { HeroPageLayout } from "@/layouts/HeroPageLayout";
import { getRecipeTypePageMetadata, RecipeType } from "@/lib/types";
import { Skeleton } from "@heroui/react";
import type { ReactElement } from "react";
import { useLocation } from "react-router";

function renderSkeletonCards(): ReactElement[] {
    return Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="w-full h-110 rounded-[5px]" />);
}

interface RecipesProps {
    fixedType?: RecipeType;
}

export const Recipes = ({ fixedType }: RecipesProps): ReactElement => {
    const location = useLocation();
    const appliedFilters = useGetAppliedFiltersFromLocation();
    const { title: pageTitle, description: pageDescription, coverImageUrl } = getRecipeTypePageMetadata(fixedType);
    const { data: recipes = [], isPending, isError } = useGetRecipes(!!fixedType);

    const canShowOutputContent = !isPending && !isError;
    const hasFilters = location.search.length > 0;
    const hasNoRecipes = canShowOutputContent && !hasFilters && recipes.length === 0;
    const hasNoResultsWithFilters = canShowOutputContent && hasFilters && recipes.length === 0;
    const recipeCards = recipes.map((recipe) => <RecipeCard2 key={recipe.id} recipe={recipe} />);
    const cardContents = isPending || (!recipes && !isError) ? renderSkeletonCards() : recipeCards;

    let resultsContent: ReactElement | null;
    if (hasNoRecipes || hasNoResultsWithFilters) {
        resultsContent = <NoResults hasNoResultsWithFilters={hasNoResultsWithFilters} recipeType={fixedType} />;
    } else {
        resultsContent = <RecipesListGrid recipeCards={cardContents} />;
    }

    return (
        <HeroPageLayout
            coverImageUrl={coverImageUrl}
            headerContent={
                <>
                    <h1 className="text-4xl xl:text-6xl xl:max-w-[575px]">{pageTitle}</h1>
                    <p className="text-lg/6 py-4 xl:mb-8 max-w-[575px] xl:max-h-[150px] overflow-y-auto">
                        {pageDescription}
                    </p>
                </>
            }
        >
            <RecipesFilterContainer fixedType={fixedType} />
            <div className="mx-auto max-w-7xl px-6 w-full">
                {appliedFilters.query && (
                    <h2 className="text-center text-3xl sm:text-4xl mb-6 sm:mb-8">
                        Results for "{appliedFilters.query}"
                    </h2>
                )}
                {resultsContent}
            </div>
        </HeroPageLayout>
    );
};
